/* Native Shopify newsletter; optional Klaviyo public client API, revision 2026-10-15. */
if (!customElements.get('ooga-birthday-popup')) {
  customElements.define('ooga-birthday-popup', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      this.cleanup = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog || !this.form) return;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.storageKey = `ooga-birthday-${this.config.sectionId}`;
      this.nativePendingKey = `${this.storageKey}-pending`;
      this.details = this.form.querySelector('.obp-birthday-details');
      this.dataset.subscriptionMethod = this.form.hasAttribute('data-klaviyo-form') ? 'klaviyo' : 'shopify';
      if (!this.config.preview && this.dataset.subscriptionMethod === 'shopify') {
        // The native newsletter collects email only; do not discard birthday data silently.
        this.details.open = false;
        this.details.hidden = true;
        const personal = this.form.querySelector('[name=personal_message]');
        if (personal) {
          personal.disabled = true;
          personal.closest('.obp-field').hidden = true;
        }
      }
      this.button = this.form.querySelector('[type=submit]');
      this.button.disabled = false;
      this.sending = false;
      if (!this.config.preview && this.dataset.subscriptionMethod === 'shopify') {
        this.nativeBackdrop = document.createElement('div');
        this.nativeBackdrop.className = 'obp-native-backdrop';
        this.nativeBackdrop.hidden = true;
        this.prepend(this.nativeBackdrop);
      }
      const listen = (el, type, handler) => el?.addEventListener(type, handler, {signal: this.cleanup.signal});
      const rememberDismissal = () => {
        if (!this.config.preview && !this.submitted && !this.nativeSubmitting) this.remember({dismissedUntil: Date.now() + 30 * 86400000});
      };
      const dismiss = () => {
        rememberDismissal();
        this.dialog.close();
      };
      listen(this.querySelector('[data-open]'), 'click', () => this.open());
      listen(this.querySelector('[data-close]'), 'click', dismiss);
      listen(this.querySelector('[data-accept]'), 'click', () => this.setStep('signup', true));
      this.querySelectorAll('[data-decline]').forEach(el => listen(el, 'click', dismiss));
      listen(this.dialog, 'click', event => {
        const box = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) dismiss();
      });
      listen(this.dialog, 'close', () => {
        if (this.nativeBackdrop) this.nativeBackdrop.hidden = true;
        rememberDismissal();
      });
      listen(this.dialog, 'cancel', rememberDismissal);
      listen(this.nativeBackdrop, 'click', dismiss);
      listen(this.dialog, 'keydown', event => {
        if (this.nativeBackdrop && event.key === 'Escape' && !event.defaultPrevented) {
          event.preventDefault();
          dismiss();
        }
      });
      listen(this.button, 'click', () => {
        if (this.nativeBackdrop && this.form.checkValidity()) this.prepareNativeSubmit();
      });
      listen(this.form, 'submit', event => this.submit(event));
      listen(this.details, 'toggle', () => this.syncBirthdayFields());
      listen(this.form, 'input', () => this.form.querySelectorAll('input,select').forEach(el => el.setCustomValidity('')));
      listen(document, 'shopify:section:select', event => {
        if (event.detail?.sectionId === this.config.sectionId) this.open();
      });
      listen(document, 'shopify:section:deselect', event => {
        if (event.detail?.sectionId === this.config.sectionId) this.dialog.close();
      });
      this.syncBirthdayFields();
      const note = this.querySelector('[data-connection-status]');
      if (note) note.hidden = this.ready();
      if (!this.config.preview && this.finishNativeReturn()) return;
      if (this.config.preview && this.config.previewOpen !== false) this.open();
      else if (!this.config.preview && this.config.enabled && !this.suppressed()) {
        this.timer = setTimeout(() => this.open(), this.config.delay * 1000);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.requestAbort?.abort();
      this.cleanup?.abort();
      this.cleanup = null;
      if (this.dialog?.open) this.dialog.close();
    }

    ready() {
      const validId = value => /^[a-zA-Z0-9]{1,32}$/.test(String(value || '').trim());
      return validId(this.config.publicKey) && validId(this.config.listId);
    }

    finishNativeReturn() {
      const result = this.form.querySelector('[data-native-result]');
      if (!result) return false;
      let pending = 0;
      try { pending = Number(sessionStorage.getItem(this.nativePendingKey)); } catch {}
      const recent = pending > 0 && Date.now() - pending < 15 * 60000;
      const matchesForm = location.hash === `#${this.form.id}`;
      if (!recent && !matchesForm) return false;
      const error = result.querySelector('[data-native-error]');
      if (result.dataset.success !== 'true' && !error) return false;
      try { sessionStorage.removeItem(this.nativePendingKey); } catch {}
      if (!this.dialog.open) this.showDialog();
      if (result.dataset.success === 'true') {
        // Only Shopify's server-rendered form result confirms the native submission.
        this.submitted = true;
        this.remember({submitted: true});
        this.querySelector('[data-step=success] .obp-description').textContent = this.config.shopifySuccess;
        this.setStep('success', true);
      } else {
        this.setStep('signup', true);
        this.querySelector('[data-status]').textContent = error.textContent;
      }
      return true;
    }

    remember(state) {
      this.memoryState = state;
      try { localStorage.setItem(this.storageKey, JSON.stringify(state)); } catch {}
    }

    showDialog() {
      if (this.nativeBackdrop) {
        // A modal dialog makes Shopify's external CAPTCHA frames inert and hides them.
        this.nativeBackdrop.hidden = false;
        this.dialog.show();
      } else this.dialog.showModal();
    }

    prepareNativeSubmit() {
      this.nativeSubmitting = true;
      try { sessionStorage.setItem(this.nativePendingKey, String(Date.now())); } catch {}
      // Let Shopify display its security challenge with no popup covering it.
      this.dialog.close();
    }

    suppressed() {
      let state = this.memoryState || {};
      try { state = JSON.parse(localStorage.getItem(this.storageKey) || 'null') || state; } catch {}
      return !!(this.config.knownSubscriber || state.submitted || state.dismissedUntil > Date.now());
    }

    syncBirthdayFields() {
      const active = this.details.open && !this.details.hidden;
      this.details.querySelector('.obp-birthday-fields').inert = !active;
      this.details.querySelectorAll('input,select').forEach(el => {
        el.required = active;
        el.disabled = !active;
        el.setCustomValidity('');
      });
    }

    setStep(step, focus = false) {
      const panel = this.querySelector(`[data-step="${step}"]`);
      if (!panel) return;
      this.querySelectorAll('[data-step]').forEach(el => el.hidden = el !== panel);
      this.dialog.setAttribute('aria-labelledby', panel.querySelector('h2').id);
      this.dialog.setAttribute('aria-describedby', panel.querySelector('.obp-description').id);
      if (focus) (step === 'signup' ? this.form.querySelector('[data-email]') : panel.querySelector('h2')).focus();
    }

    open() {
      if (!this.isConnected || this.dialog.open) return;
      if (!this.config.preview && (!this.config.enabled || this.suppressed())) return;
      if (document.querySelector('ooga-birthday-popup dialog[open]')) return;
      this.setStep('offer');
      this.showDialog();
    }

    birthday() {
      if (!this.details.open) return null;
      const yearInput = this.form.querySelector('[data-year]');
      const year = Number(yearInput.value);
      const month = Number(this.form.querySelector('[data-month]').value);
      const day = Number(this.form.querySelector('[data-day]').value);
      const date = new Date(0);
      date.setUTCFullYear(year, month - 1, day);
      date.setUTCHours(0, 0, 0, 0);
      const now = new Date();
      const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
      const valid = Number.isInteger(year) && year >= 1 && year <= 9999 && date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day && date <= today;
      yearInput.setCustomValidity(valid ? '' : this.config.invalidDate);
      return valid ? `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
    }

    subscriptionPayload() {
      const attributes = {
        email: this.form.querySelector('[data-email]').value.trim(),
        subscriptions: {email: {marketing: {consent: 'SUBSCRIBED'}}},
        properties: {
          ooga_signup_source: 'ooga_birthday_invitation',
          ooga_popup_section: this.config.sectionId,
          ooga_gift_delivery: this.config.delivery,
          ooga_gift_days_before: this.config.daysBefore,
          ooga_gift_mode: this.config.giftMode
        }
      };
      const couponCode = String(this.config.couponCode || '').trim();
      if (couponCode && couponCode.length <= 255) attributes.properties.ooga_coupon_code = couponCode;
      if (this.details.open) {
        attributes.first_name = this.form.querySelector('[name=first_name]').value.trim();
        attributes.properties.Birthday = this.birthday();
      }
      const message = this.form.querySelector('[name=personal_message]');
      if (this.config.giftMode === 'personalized' && message?.value.trim()) attributes.properties.ooga_gift_message = message.value.trim();
      return {
        data: {
          type: 'subscription',
          attributes: {custom_source: 'ooga_birthday_invitation', profile: {data: {type: 'profile', attributes}}},
          relationships: {list: {data: {type: 'list', id: String(this.config.listId || '').trim()}}}
        }
      };
    }

    async submit(event) {
      if (!this.config.preview && this.dataset.subscriptionMethod === 'shopify') {
        if (!this.config.enabled) { event.preventDefault(); return; }
        // Keep the normal Shopify POST, validation and spam protection intact.
        this.prepareNativeSubmit();
        return;
      }
      event.preventDefault();
      if (this.sending) return;
      this.syncBirthdayFields();
      const email = this.form.querySelector('[data-email]');
      email.value = email.value.trim();
      if (this.details.open) {
        const name = this.form.querySelector('[name=first_name]');
        name.setCustomValidity(name.value.trim() ? '' : this.config.requiredName);
        this.birthday();
      }
      if (!this.form.reportValidity()) return;
      const payload = this.subscriptionPayload();
      const status = this.querySelector('[data-status]');
      if (this.config.preview) {
        status.textContent = this.config.previewMessage;
        this.dispatchEvent(new CustomEvent('ooga:birthday-preview', {detail: payload}));
        this.setStep('success', true);
        return;
      }
      if (!this.config.enabled || !this.ready()) { status.textContent = this.config.errorMessage; return; }
      this.sending = true;
      this.button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      status.textContent = '';
      this.requestAbort = new AbortController();
      const timeout = setTimeout(() => this.requestAbort.abort(), 15000);
      try {
        const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.config.publicKey.trim())}`;
        const response = await fetch(url, {
          method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer',
          headers: {'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2026-10-15'},
          body: JSON.stringify(payload), signal: this.requestAbort.signal
        });
        if (response.status !== 202) throw new Error('Subscription request was not accepted');
        if (!this.isConnected) return;
        // 202 is acceptance, not proof of persistence, flow eligibility or email delivery.
        this.submitted = true;
        this.remember({submitted: true});
        status.textContent = this.config.successMessage;
        this.form.reset();
        this.details.open = false;
        this.syncBirthdayFields();
        this.setStep('success', true);
      } catch {
        if (this.isConnected) status.textContent = this.config.errorMessage;
      } finally {
        clearTimeout(timeout);
        this.sending = false;
        if (this.isConnected) {
          this.button.disabled = false;
          this.form.removeAttribute('aria-busy');
        }
      }
    }
  });
}
